import { DataGrid, Column, Editing, Scrolling } from "devextreme-react/data-grid";
import { SearchSelectBox } from "./SearchSelectBox";
import { FilterNavigationHeader } from './FilterNavigationHeader';
import { useEffect, useState } from "react";


export const CopyPasteGrid = (props) => {
    const { 
        data, 
        setData, 
        typeOfSearchColumnName, 
        typeOfSearchColumn, 
        shouldShowError, 
        setFilterName, 
        setSelectedSearch,
        onNext,
        onBack,
        onClearData,
        currentStep = 2,
        totalSteps = 3,
        stepTitle = "Select Values"
    } = props;

    const [searchType, setSearchType] = useState(null);
    
    // Set search type based on typeOfSearchColumn
    useEffect(() => {
        if (typeOfSearchColumn === 'partName') {
            setSearchType('part');
        }
        else if (typeOfSearchColumn === 'internalId') {
            setSearchType('internal');
        }
    }, [typeOfSearchColumn]);

    const onCellPrepared = (e) => {
        if (e?.rowType !== 'data') return;

        // Apply alternating row styling with better colors
        if (e.isAltRow) {
            e.cellElement.style.backgroundColor = "#f8f9fa";
        }

        // Apply error styling with better accessibility
        if (e.data?.exist === 'No' && shouldShowError) {
            e.cellElement.style.color = "#dc3545";
            e.cellElement.style.fontWeight = "500";
            e.cellElement.setAttribute('aria-label', 'Invalid data entry');
        }
    };

    const handlePaste = async (e) => {
        try {
            // Check clipboard API availability
            if (!navigator.clipboard) {
                console.error('Clipboard API not available');
                return;
            }

            const clipBoard = await navigator.clipboard.readText();
            
            if (!clipBoard.trim()) {
                console.warn('Clipboard is empty');
                return;
            }

            // Sanitize input to prevent XSS
            const sanitizedClipboard = clipBoard
                .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '') // Remove scripts
                .replace(/[<>]/g, ''); // Remove HTML tags

            const rows = sanitizedClipboard.split("\n").filter(row => row.trim());
            
            // Limit rows to prevent memory issues
            const maxRows = 5000;
            if (rows.length > maxRows) {
                console.warn(`Too many rows (${rows.length}). Limiting to ${maxRows}`);
                rows.splice(maxRows);
            }

            const result = rows.map((row, index) => {
                const cells = row.split("\t");
                const value = cells[0]?.trim();
                
                if (!value) {
                    return null; // Skip empty values
                }

                if (typeOfSearchColumn === 'partName') {
                    return {
                        partName: value,
                        id: `${Date.now()}-${index}` // Add unique ID for React keys
                    };
                }
                else {
                    return {
                        internalId: value,
                        id: `${Date.now()}-${index}` // Add unique ID for React keys
                    };
                }
            }).filter(Boolean); // Remove null entries

            // Avoid duplicates by checking existing values
            const existingValues = new Set();
            if (data && data.length > 0) {
                data.forEach(item => {
                    const value = item[typeOfSearchColumn];
                    if (value) existingValues.add(value);
                });
            }

            // Filter out duplicates from new data
            const uniqueNewData = result.filter(item => {
                const value = item[typeOfSearchColumn];
                return value && !existingValues.has(value);
            });

            if (uniqueNewData.length === 0) {
                console.info('No new unique data to add');
                return;
            }

            // Merge with existing data
            const finalData = [...(data || []), ...uniqueNewData];
            setData(finalData);
            
            console.log(`Added ${uniqueNewData.length} new unique items`);

        } catch (error) {
            console.error('Paste operation failed:', error);
        }
    };

    const handleSelectedSearchChange = (selectedSearchName, selectedSearch) => {
        try {
            if (selectedSearchName === null) {
                setData([]);
                setFilterName(null);
                setSelectedSearch(null);
                console.log('Search cleared');
                return;
            }

            // Validate search data structure
            if (!selectedSearch?.query || !Array.isArray(selectedSearch.query)) {
                console.error('Invalid search data format');
                return;
            }

            // Add id field to loaded search data if missing
            const dataWithIds = selectedSearch.query.map((item, index) => {
                if (!item.id) {
                    return {
                        ...item,
                        id: `search-${Date.now()}-${index}`
                    };
                }
                return item;
            });

            setData(dataWithIds);
            setFilterName(selectedSearchName);
            setSelectedSearch(selectedSearch);
            
            console.log(`Loaded search: ${selectedSearchName} with ${dataWithIds.length} items`);
        } catch (error) {
            console.error('Search change failed:', error);
        }
    };

    return (
        <div style={{ 
            position: 'relative',
            maxWidth: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
        }}>
            {/* Navigation Header */}
            <FilterNavigationHeader
                stepTitle={stepTitle}
                currentStep={currentStep}
                totalSteps={totalSteps}
                activeFiltersCount={data?.length || 0}
                onBack={onBack}
                onNext={onNext}
                showApply={false}
            />

            {/* Main Content Area */}
            <div style={{ 
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem', // Reduced gap
                padding: '1rem 0 1.5rem 0' // Reduced padding
            }}>
                {/* Search Selection Card - Compact Layout */}
                <div style={{ 
                    backgroundColor: '#ffffff',
                    borderRadius: '8px',
                    border: '1px solid #e9ecef',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                }}>
                    <div style={{
                        padding: '0.75rem 1rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem',
                        flexWrap: 'wrap'
                    }}>
                        {/* Compact Label */}
                        <span style={{ 
                            fontWeight: '500',
                            color: '#495057',
                            fontSize: '0.9rem',
                            whiteSpace: 'nowrap',
                            minWidth: 'fit-content'
                        }}>
                            Saved Search:
                        </span>
                        
                        {/* Search Select Box - Takes remaining space */}
                        <div style={{ 
                            flex: '1', 
                            minWidth: '200px',
                            maxWidth: '400px'
                        }}>
                            <SearchSelectBox
                                type={searchType}
                                onSelectedSearchChange={handleSelectedSearchChange}
                            />
                        </div>
                        
                        {/* Clear Data Button */}
                        {data?.length > 0 && onClearData && (
                            <button
                                onClick={onClearData}
                                style={{
                                    background: '#fff',
                                    border: '1px solid #dc3545',
                                    color: '#dc3545',
                                    padding: '0.35rem 0.75rem',
                                    borderRadius: '6px',
                                    fontSize: '0.8rem',
                                    fontWeight: '500',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    whiteSpace: 'nowrap'
                                }}
                                onMouseOver={(e) => {
                                    e.target.style.backgroundColor = '#dc3545';
                                    e.target.style.color = 'white';
                                }}
                                onMouseOut={(e) => {
                                    e.target.style.backgroundColor = '#fff';
                                    e.target.style.color = '#dc3545';
                                }}
                            >
                                Clear ({data.length})
                            </button>
                        )}
                    </div>
                </div>

                {/* Data Input Card - Compact Layout */}
                <div style={{
                    backgroundColor: '#ffffff',
                    borderRadius: '8px',
                    border: '1px solid #e9ecef',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    {/* Compact Header */}
                    <div style={{
                        padding: '0.75rem 1rem',
                        borderBottom: '1px solid #f1f3f4',
                        backgroundColor: '#fafbfc'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '1rem'
                        }}>
                            <h4 style={{ 
                                fontWeight: '600',
                                color: '#2c3e50',
                                margin: 0,
                                fontSize: '0.95rem'
                            }}>
                                {typeOfSearchColumnName} Data Entry
                            </h4>
                            <span style={{
                                color: '#6c757d',
                                fontSize: '0.8rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.25rem'
                            }}>
                                Press <kbd style={{
                                    backgroundColor: '#e9ecef',
                                    border: '1px solid #ced4da',
                                    borderRadius: '3px',
                                    padding: '0.1rem 0.3rem',
                                    fontSize: '0.75rem',
                                    fontWeight: '500'
                                }}>Ctrl+V</kbd> to paste
                            </span>
                        </div>
                    </div>
                    
                    {/* Data Grid Container */}
                    <div style={{ 
                        flex: 1,
                        margin: '0.75rem' // Reduced margin
                    }}>
                        <div 
                            className="ConsolidateGrid" 
                            onPaste={handlePaste}
                            style={{
                                border: '2px dashed #cbd5e0',
                                borderRadius: '12px',
                                position: 'relative',
                                maxHeight: '30vh',
                                transition: 'all 0.2s ease',
                                backgroundColor: data?.length > 0 ? '#f8f9fa' : '#fafbfc'
                            }}
                            role="region"
                            aria-label="Data input area - paste data here"
                            tabIndex={0}
                            onFocus={(e) => {
                                e.target.style.borderColor = '#4a90e2';
                                e.target.style.backgroundColor = '#f8f9fa';
                            }}
                            onBlur={(e) => {
                                e.target.style.borderColor = '#cbd5e0';
                                e.target.style.backgroundColor = data?.length > 0 ? '#f8f9fa' : '#fafbfc';
                            }}
                        >
                            <DataGrid
                                dataSource={data || []}
                                showBorders={true}
                                columnMinWidth={120}
                                columnAutoWidth={true}
                                height="30vh"
                                width="100%"
                                columnResizingMode="widget"
                                wordWrapEnabled={true}
                                onCellPrepared={(e) => onCellPrepared(e)}
                                noDataText={`Copy and paste your ${typeOfSearchColumnName} data here using Ctrl + V`}
                                keyExpr={data && data.length > 0 && data[0].hasOwnProperty('id') ? "id" : typeOfSearchColumn}
                                focusedRowEnabled={true}
                                hoverStateEnabled={true}
                            >
                                <Editing 
                                    mode="cell" 
                                    allowUpdating={true} 
                                    allowAdding={true} 
                                    allowDeleting={true}
                                    confirmDelete={true}
                                />
                                <Scrolling 
                                    mode="virtual" 
                                    showScrollbar="onHover"
                                />
                                <Column
                                    dataField={typeOfSearchColumn}
                                    caption={typeOfSearchColumnName}
                                    alignment="left"
                                    allowSorting={true}
                                    allowFiltering={true}
                                />
                            </DataGrid>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
